import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import StarRating from "../components/ui/StarRating";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import logger from "../utils/logger.js";
import { setupMessagePolling, stopMessagePolling } from "../utils/echo.js";
import {
  SERVICE_ORDER_ATTACHMENT_ACCEPT,
  validateServiceOrderAttachment,
} from "../utils/serviceOrderAttachments.js";
import {
  createServiceOrderMilestone,
  formatServiceCurrency,
  getFreelancerServiceTitle,
  getMyFreelancerOrders,
  getServiceOrderDetails,
  updateServiceOrderStatus,
  getOrderMessages,
  sendOrderMessage,
  findRecoveredOrderMessage,
  markOrderMessagesRead,
  deliverServiceOrder,
  respondOrderCancellation,
} from "../api/freelancerServices";
import {
  ArrowLeft,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock3,
  DollarSign,
  Download,
  Flag,
  MessageCircle,
  Milestone,
  Paperclip,
  Send,
  ShieldCheck,
  Star,
  Truck,
  User,
  AlertTriangle,
  XCircle,
  Wallet,
  BarChart3,
  Loader2,
  Store,
} from "lucide-react";
import "./MyFreelancerOrders.css";

/* ─── helpers ─────────────────────────────────────── */
function getStatusMeta(status, isAr) {
  const map = {
    pending:               { cls: "mfo-badge--warning",  label: isAr ? "قيد الانتظار"  : "Pending" },
    paid:                  { cls: "mfo-badge--info",     label: isAr ? "مدفوع"         : "Paid" },
    in_progress:           { cls: "mfo-badge--primary",  label: isAr ? "قيد التنفيذ"  : "In Progress" },
    delivered:             { cls: "mfo-badge--success",  label: isAr ? "تم التسليم"   : "Delivered" },
    completed:             { cls: "mfo-badge--success",  label: isAr ? "مكتمل"         : "Completed" },
    cancelled:             { cls: "mfo-badge--danger",   label: isAr ? "ملغي"          : "Cancelled" },
    disputed:              { cls: "mfo-badge--danger",   label: isAr ? "نزاع"          : "Disputed" },
    revision_requested:    { cls: "mfo-badge--warning",  label: isAr ? "طلب مراجعة"   : "Revision Requested" },
    cancellation_requested:{ cls: "mfo-badge--warning",  label: isAr ? "طلب إلغاء"    : "Cancellation Requested" },
  };
  return map[String(status || "").toLowerCase()] || { cls: "mfo-badge--muted", label: status || (isAr ? "غير معروف" : "Unknown") };
}

function getPaymentMeta(status, isAr) {
  if (status === "paid")     return { cls: "mfo-badge--success", label: isAr ? "مدفوع"    : "Paid" };
  if (status === "refunded") return { cls: "mfo-badge--danger",  label: isAr ? "مسترد"    : "Refunded" };
  return { cls: "mfo-badge--warning", label: isAr ? "قيد الإنتظار" : "Pending" };
}

function getNextActions(status) {
  const s = String(status || "").toLowerCase();
  if (s === "paid")               return ["in_progress"];
  if (s === "delivered")          return ["completed"];
  if (s === "revision_requested") return ["in_progress"];
  return [];
}

function getStorageBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL || "/laravel-api/api";
  return apiUrl.replace(/\/api(\/v1)?$/, "");
}

function createMilestoneDraft() {
  return { title: "", description: "", amount: "", dueAt: "" };
}

function canManageMilestones(order) {
  const status = String(order?.status || "").toLowerCase();
  const paymentStatus = String(order?.payment_status || "").toLowerCase();
  const hasActiveDispute = Array.isArray(order?.disputes)
    && order.disputes.some((d) => ["open", "under_review"].includes(String(d?.status || "").toLowerCase()));
  if (paymentStatus !== "paid" || hasActiveDispute) return false;
  return ["paid", "in_progress", "delivered"].includes(status);
}

const ACTION_LABELS = (isAr) => ({
  in_progress: isAr ? "بدء التنفيذ" : "Start Executing",
  completed:   isAr ? "إكمال الطلب" : "Mark as Complete",
});

/* ─── component ───────────────────────────────────── */
export default function MyFreelancerOrders() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const isAr = lang === "ar";
  const { orderId: orderIdParam } = useParams();
  const orderId = orderIdParam ? Number(orderIdParam) : null;

  const [orders, setOrders]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [notice, setNotice]         = useState("");
  const [actionOrderId, setActionOrderId] = useState(0);
  const [expandedOrderId, setExpandedOrderId] = useState(0);
  const [detailLoadingId, setDetailLoadingId] = useState(0);
  const [milestoneDrafts, setMilestoneDrafts] = useState({});
  const [deliveryDrafts, setDeliveryDrafts]   = useState({});
  const [orderMessages, setOrderMessages]     = useState({});
  const [messageTexts, setMessageTexts]       = useState({});
  const [messageFiles, setMessageFiles]       = useState({});
  const [messageFileErrors, setMessageFileErrors] = useState({});

  function handleMessageFileChange(oid, file, input) {
    if (!file) {
      setMessageFiles((c) => ({ ...c, [oid]: null }));
      setMessageFileErrors((c) => ({ ...c, [oid]: "" }));
      return;
    }
    const err = validateServiceOrderAttachment(file);
    if (err) {
      setMessageFiles((c) => ({ ...c, [oid]: null }));
      setMessageFileErrors((c) => ({ ...c, [oid]: err }));
      if (input) input.value = "";
      return;
    }
    setMessageFiles((c) => ({ ...c, [oid]: file }));
    setMessageFileErrors((c) => ({ ...c, [oid]: "" }));
  }

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        setLoading(true); setError("");
        let data;
        if (orderId) {
          const details = await getServiceOrderDetails(orderId);
          data = [details];
          if (active) {
            setExpandedOrderId(Number(orderId));
            try {
              const msgs = await getOrderMessages(orderId);
              setOrderMessages((c) => ({ ...c, [orderId]: msgs }));
              await markOrderMessagesRead(orderId);
            } catch (e) { logger.error("msgs", e); }
          }
        } else {
          data = await getMyFreelancerOrders();
        }
        if (active) setOrders(data);
      } catch (err) {
        logger.error("My freelancer orders error", err);
        if (active) setError(isAr ? "تعذر تحميل طلباتك كمستقل." : "Unable to load your freelancer orders.");
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [isAr, orderId]);

  useEffect(() => {
    if (!expandedOrderId) return;

    try {
      setupMessagePolling(expandedOrderId, (polledMessages) => {
        if (Array.isArray(polledMessages)) {
          setOrderMessages((c) => ({ ...c, [expandedOrderId]: polledMessages }));
        }
      });
    } catch (err) {
      logger.error("Setup message polling error in MyFreelancerOrders", err);
    }

    return () => {
      stopMessagePolling(expandedOrderId);
    };
  }, [expandedOrderId]);

  function updateOrderState(oid, updater) {
    setOrders((c) => c.map((o) => (o.id === oid ? updater(o) : o)));
  }

  async function handleToggleDetails(oid) {
    if (expandedOrderId === oid) { setExpandedOrderId(0); return; }
    try {
      setDetailLoadingId(oid); setError("");
      const details = await getServiceOrderDetails(oid);
      updateOrderState(oid, () => details);
      setExpandedOrderId(oid);
      try {
        const msgs = await getOrderMessages(oid);
        setOrderMessages((c) => ({ ...c, [oid]: msgs }));
        await markOrderMessagesRead(oid);
      } catch (e) { logger.error("msgs", e); }
    } catch (err) {
      logger.error("Freelancer order details error", err);
      setError(err?.message || (isAr ? "تعذر تحميل التفاصيل." : "Unable to load the order details."));
    } finally { setDetailLoadingId(0); }
  }

  async function handleStatusUpdate(oid, status) {
    try {
      setActionOrderId(oid); setError(""); setNotice("");
      const result = await updateServiceOrderStatus(oid, { status });
      const next = result?.data || null;
      if (next) {
        updateOrderState(oid, (cur) => ({
          ...cur, ...next,
          milestones: Array.isArray(next.milestones) ? next.milestones : cur?.milestones || [],
          disputes:   Array.isArray(next.disputes)   ? next.disputes   : cur?.disputes   || [],
          refunds:    Array.isArray(next.refunds)     ? next.refunds    : cur?.refunds    || [],
          review:     next.review ?? cur?.review ?? null,
          payment_summary: next.payment_summary ?? cur?.payment_summary ?? null,
        }));
      }
      setNotice(result?.message || (isAr ? "تم تحديث حالة الطلب بنجاح." : "Order status updated successfully."));
    } catch (err) {
      logger.error("status update", err);
      setError(err?.message || (isAr ? "تعذر تحديث الحالة." : "Unable to update the status."));
    } finally { setActionOrderId(0); }
  }

  function updateMilestoneDraft(oid, field, value) {
    setMilestoneDrafts((c) => ({ ...c, [oid]: { ...(c[oid] || createMilestoneDraft()), [field]: value } }));
  }

  async function handleCreateMilestone(oid) {
    const draft = milestoneDrafts[oid] || createMilestoneDraft();
    try {
      setActionOrderId(oid); setError(""); setNotice("");
      const milestone = await createServiceOrderMilestone(oid, {
        title: draft.title.trim(),
        description: draft.description.trim() || undefined,
        amount: Number(draft.amount),
        due_at: draft.dueAt || undefined,
      });
      updateOrderState(oid, (o) => ({ ...o, milestones: [...(Array.isArray(o.milestones) ? o.milestones : []), milestone] }));
      setMilestoneDrafts((c) => ({ ...c, [oid]: createMilestoneDraft() }));
      setNotice(isAr ? "تمت إضافة المعلم بنجاح." : "Milestone created successfully.");
    } catch (err) {
      logger.error("Create milestone", err);
      setError(err?.message || (isAr ? "تعذر إنشاء المعلم." : "Unable to create the milestone."));
    } finally { setActionOrderId(0); }
  }

  async function handleDeliver(oid) {
    const draft = deliveryDrafts[oid] || {};
    if (!draft.files || draft.files.length === 0) return;
    try {
      setActionOrderId(oid); setError(""); setNotice("");
      const fd = new FormData();
      draft.files.forEach((f) => fd.append("files[]", f));
      if (draft.notes?.trim()) fd.append("delivery_notes", draft.notes);
      const result = await deliverServiceOrder(oid, fd);
      const next = result?.data || result || null;
      if (next) updateOrderState(oid, (cur) => ({ ...cur, ...next, status: next.status || cur.status, delivery_files: next.delivery_files || cur.delivery_files }));
      setDeliveryDrafts((c) => ({ ...c, [oid]: {} }));
      setNotice(isAr ? "تم تسليم الملفات بنجاح." : "Delivery submitted successfully.");
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Deliver", err);
      setError(err?.message || (isAr ? "تعذر تسليم الملفات." : "Unable to submit delivery."));
    } finally { setActionOrderId(0); }
  }

  async function handleSendMessage(oid) {
    if (actionOrderId === oid) return;
    const text = (messageTexts[oid] || "").trim();
    if (!text && !messageFiles[oid]) return;
    const prevMsgs = Array.isArray(orderMessages[oid]) ? orderMessages[oid] : [];
    const hadAttachment = Boolean(messageFiles[oid]);
    try {
      setActionOrderId(oid); setError("");
      const fd = new FormData();
      fd.append("message", text);
      if (messageFiles[oid]) fd.append("attachment", messageFiles[oid]);
      const newMsg = await sendOrderMessage(oid, fd);
      const latestMsgs = await getOrderMessages(oid).catch(() => null);
      if (Array.isArray(latestMsgs)) {
        setOrderMessages((c) => ({ ...c, [oid]: latestMsgs }));
      } else if (newMsg) {
        setOrderMessages((c) => ({ ...c, [oid]: [...(c[oid] || []), newMsg] }));
      }
      setMessageTexts((c) => ({ ...c, [oid]: "" }));
      setMessageFiles((c) => ({ ...c, [oid]: null }));
      setMessageFileErrors((c) => ({ ...c, [oid]: "" }));
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Send message", err);
      try {
        const latest = await getOrderMessages(oid);
        const recovered = findRecoveredOrderMessage(prevMsgs, latest, text, hadAttachment);
        if (recovered) {
          setOrderMessages((c) => ({ ...c, [oid]: latest }));
          setMessageTexts((c) => ({ ...c, [oid]: "" }));
          setMessageFiles((c) => ({ ...c, [oid]: null }));
          setMessageFileErrors((c) => ({ ...c, [oid]: "" }));
          window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
          return;
        }
      } catch (e) { logger.error("Refresh msgs", e); }
      setError(err?.message || (isAr ? "تعذر إرسال الرسالة." : "Unable to send message."));
    } finally { setActionOrderId(0); }
  }

  async function handleRespondCancellation(oid, action) {
    try {
      setActionOrderId(oid); setError(""); setNotice("");
      const result = await respondOrderCancellation(oid, { action });
      const next = result?.data || result || null;
      if (next) updateOrderState(oid, (cur) => ({ ...cur, ...next, status: next.status || cur.status }));
      setNotice(result?.message || (action === "approve"
        ? (isAr ? "تم قبول طلب الإلغاء." : "Cancellation approved.")
        : (isAr ? "تم رفض طلب الإلغاء." : "Cancellation rejected.")));
      window.dispatchEvent(new CustomEvent("mg:notifications-refresh"));
    } catch (err) {
      logger.error("Respond cancellation", err);
      setError(err?.message || (isAr ? "تعذر معالجة طلب الإلغاء." : "Unable to respond to cancellation."));
    } finally { setActionOrderId(0); }
  }

  /* ── render ── */
  return (
    <div className="mfo-page" dir={isAr ? "rtl" : "ltr"}>

      {/* ══ HERO BANNER ══ */}
      <section className="mfo-hero">
        <div className="mfo-hero__glow-1" />
        <div className="mfo-hero__glow-2" />
        <div className="mfo-hero__overlay" />
        <div className="mfo-hero__inner">
          <div className="mfo-hero__text">
            <div className="mfo-hero__eyebrow">
              <Briefcase size={14} />
              {isAr ? "لوحة المستقل" : "Freelancer Panel"}
            </div>
            <h1 className="mfo-hero__title">{isAr ? "طلباتي كمستقل" : "My Freelancer Orders"}</h1>
            <p className="mfo-hero__sub">
              {isAr
                ? "أدر التنفيذ والمعالم وحالة الطلب والأرباح المحجوزة من مكان واحد."
                : "Manage fulfillment, milestones, order status, and escrowed earnings from one place."}
            </p>
          </div>
          <Link to="/freelancers" className="mfo-hero__cta">
            <Store size={16} />
            {isAr ? "سوق المستقلين" : "Marketplace"}
          </Link>
        </div>
      </section>

      {/* ══ BODY ══ */}
      <div className="mfo-shell">

        {/* Global notice / error */}
        {notice && (
          <div className="mfo-alert mfo-alert--success">
            <CheckCircle2 size={16} /><span>{notice}</span>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="mfo-center-state">
            <Loader2 size={36} className="mfo-spinner" />
            <p>{isAr ? "جارِ تحميل الطلبات..." : "Loading orders..."}</p>
          </div>
        ) : error ? (
          <div className="mfo-alert mfo-alert--error">
            <AlertTriangle size={16} /><span>{error}</span>
          </div>
        ) : orders.length === 0 ? (
          <div className="mfo-empty">
            <div className="mfo-empty__icon"><Briefcase size={36} strokeWidth={1.5} /></div>
            <h2>{isAr ? "لا توجد طلبات بعد" : "No orders yet"}</h2>
            <p>{isAr ? "ستظهر هنا الطلبات الجديدة بعد الدفع على خدماتك." : "New orders will appear here after clients pay for your services."}</p>
            <Link to="/freelancers" className="mfo-btn mfo-btn--primary mfo-btn--sm">
              <Store size={15} />{isAr ? "استعرض السوق" : "Browse Marketplace"}
            </Link>
          </div>
        ) : (
          <div className="mfo-orders-list">
            {orders.map((order) => {
              const service = order.service || {};
              const statusMeta  = getStatusMeta(order.status, isAr);
              const paymentMeta = getPaymentMeta(order.payment_status, isAr);
              const actions     = getNextActions(order.status);
              const isExpanded  = expandedOrderId === order.id;
              const milestones  = Array.isArray(order.milestones) ? order.milestones : [];
              const disputes    = Array.isArray(order.disputes)   ? order.disputes   : [];
              const refunds     = Array.isArray(order.refunds)    ? order.refunds    : [];
              const milestoneDraft = milestoneDrafts[order.id] || createMilestoneDraft();
              const milestoneEditingAllowed = canManageMilestones(order);
              const actionLabels = ACTION_LABELS(isAr);

              return (
                <article key={order.id} className={`mfo-order-card${isExpanded ? " mfo-order-card--expanded" : ""}`}>

                  {/* ── Card Header ── */}
                  <div className="mfo-order-card__header">
                    <div className="mfo-order-card__badges">
                      <span className={`mfo-badge ${statusMeta.cls}`}>{statusMeta.label}</span>
                      <span className={`mfo-badge ${paymentMeta.cls}`}>{paymentMeta.label}</span>
                      {order.payment_summary?.escrow_status && (
                        <span className="mfo-badge mfo-badge--muted">
                          <ShieldCheck size={11} />
                          {isAr ? "ضمان" : "Escrow"}: {order.payment_summary.escrow_status}
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      className="mfo-toggle-btn"
                      onClick={() => handleToggleDetails(order.id)}
                    >
                      {detailLoadingId === order.id
                        ? <><Loader2 size={14} className="mfo-spinner" />{isAr ? "جارِ التحميل..." : "Loading..."}</>
                        : isExpanded
                          ? <><ChevronUp size={14} />{isAr ? "إخفاء التفاصيل" : "Hide Details"}</>
                          : <><ChevronDown size={14} />{isAr ? "عرض التفاصيل" : "Show Details"}</>
                      }
                    </button>
                  </div>

                  {/* ── Card Body (summary) ── */}
                  <div className="mfo-order-card__body">
                    <h2 className="mfo-order-card__title">
                      {order.title || getFreelancerServiceTitle(service, isAr)}
                    </h2>

                    <div className="mfo-order-meta">
                      <span className="mfo-order-meta__item">
                        <User size={13} />
                        {isAr ? "العميل:" : "Client:"} <strong>{order.client?.name || "—"}</strong>
                      </span>
                      <span className="mfo-order-meta__item">
                        <DollarSign size={13} />
                        {isAr ? "السعر:" : "Price:"} <strong>{formatServiceCurrency(order.price || 0, order.currency || "USD")}</strong>
                      </span>
                      <span className="mfo-order-meta__item">
                        <Wallet size={13} />
                        {isAr ? "صافي أرباحك:" : "Your net:"} <strong>{formatServiceCurrency(order.payment_summary?.freelancer_amount || 0, order.payment_summary?.currency || order.currency || "USD")}</strong>
                      </span>
                    </div>

                    {/* Client brief */}
                    {order.description && (
                      <div className="mfo-brief">
                        <strong>{isAr ? "ملخص العميل:" : "Client brief:"}</strong>
                        <p>{order.description}</p>
                      </div>
                    )}

                    {/* Next status actions */}
                    {actions.length > 0 && (
                      <div className="mfo-action-row">
                        {actions.map((s) => (
                          <button
                            key={s} type="button"
                            className="mfo-btn mfo-btn--primary mfo-btn--sm"
                            disabled={actionOrderId === order.id}
                            onClick={() => handleStatusUpdate(order.id, s)}
                          >
                            {actionOrderId === order.id
                              ? <><Loader2 size={14} className="mfo-spinner" />{isAr ? "جارِ الحفظ..." : "Saving..."}</>
                              : <><CheckCircle2 size={14} />{actionLabels[s] || s}</>
                            }
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Delivery section */}
                    {(order.status === "in_progress" || order.status === "revision_requested") && (
                      <div className="mfo-delivery-box">
                        <div className="mfo-delivery-box__head">
                          <Truck size={15} />
                          {isAr ? "تسليم الملفات" : "Deliver Files"}
                        </div>
                        <textarea
                          rows="2"
                          className="mfo-input"
                          placeholder={isAr ? "ملاحظات التسليم (اختياري)" : "Delivery notes (optional)"}
                          value={deliveryDrafts[order.id]?.notes || ""}
                          onChange={(e) => setDeliveryDrafts((c) => ({ ...c, [order.id]: { ...c[order.id], notes: e.target.value } }))}
                        />
                        <input
                          type="file" multiple className="mfo-file-input"
                          onChange={(e) => setDeliveryDrafts((c) => ({ ...c, [order.id]: { ...c[order.id], files: Array.from(e.target.files || []) } }))}
                        />
                        <button
                          type="button"
                          className="mfo-btn mfo-btn--success mfo-btn--sm"
                          disabled={actionOrderId === order.id || !deliveryDrafts[order.id]?.files?.length}
                          onClick={() => handleDeliver(order.id)}
                        >
                          {actionOrderId === order.id
                            ? <><Loader2 size={14} className="mfo-spinner" />{isAr ? "جارِ التسليم..." : "Delivering..."}</>
                            : <><Truck size={14} />{isAr ? "تسليم الملفات" : "Submit Delivery"}</>
                          }
                        </button>
                      </div>
                    )}

                    {/* Cancellation request */}
                    {order.cancellation_requested_by && (
                      <div className="mfo-alert mfo-alert--warning">
                        <AlertTriangle size={15} />
                        <div>
                          <strong>{isAr ? "طلب إلغاء معلق" : "Pending Cancellation Request"}</strong>
                          <p className="mfo-alert__sub">{isAr ? "السبب:" : "Reason:"} {order.cancellation_reason || (isAr ? "لم يتم تحديد سبب" : "No reason provided")}</p>
                          <div className="mfo-action-row" style={{ marginTop: "10px" }}>
                            <button type="button" className="mfo-btn mfo-btn--success mfo-btn--sm" disabled={actionOrderId === order.id} onClick={() => handleRespondCancellation(order.id, "approve")}>
                              <CheckCircle2 size={13} />{actionOrderId === order.id ? (isAr ? "جارِ المعالجة..." : "Processing...") : (isAr ? "قبول الإلغاء" : "Approve")}
                            </button>
                            <button type="button" className="mfo-btn mfo-btn--danger mfo-btn--sm" disabled={actionOrderId === order.id} onClick={() => handleRespondCancellation(order.id, "reject")}>
                              <XCircle size={13} />{actionOrderId === order.id ? (isAr ? "جارِ المعالجة..." : "Processing...") : (isAr ? "رفض الإلغاء" : "Reject")}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Expanded Details ── */}
                  {isExpanded && (
                    <div className="mfo-order-card__details">

                      {/* Messages */}
                      <section className="mfo-section">
                        <div className="mfo-section__head">
                          <MessageCircle size={16} />
                          <h3>{isAr ? "الرسائل" : "Messages"}</h3>
                        </div>

                        <div className="mfo-chat">
                          {Array.isArray(orderMessages[order.id]) && orderMessages[order.id].length > 0 ? (
                            orderMessages[order.id].map((msg) => {
                              const isOwn =
                                Number(msg.sender_id) === Number(user?.id) ||
                                Number(msg.sender_id) === Number(order.freelancer?.id);

                              const senderName =
                                msg.sender?.name ||
                                (isOwn
                                  ? isAr
                                    ? "أنت"
                                    : "You"
                                  : Number(msg.sender_id) === Number(order.client?.id)
                                    ? order.client?.name
                                    : isAr
                                      ? "العميل"
                                      : "Client");

                              const attachUrl =
                                msg.attachment_url ||
                                (msg.attachment_path || msg.file?.path
                                  ? `${getStorageBaseUrl()}/storage/${String(msg.attachment_path || msg.file?.path).replace(/^\/?(storage\/)?/i, "")}`
                                  : null);

                              const mime = String(msg.attachment_mime || msg.file?.mime || "").toLowerCase();
                              const fileName = msg.attachment_name || msg.file?.original_name || (isAr ? "مرفق" : "Attachment");
                              const isImg =
                                mime.startsWith("image/") ||
                                /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(String(fileName).toLowerCase());

                              return (
                                <div key={msg.id || msg.created_at} className={`mfo-msg${isOwn ? " mfo-msg--own" : ""}`}>
                                  <div className="mfo-msg__meta">
                                    <span className="mfo-msg__name">{senderName}</span>
                                    {msg.created_at && (
                                      <span className="mfo-msg__time">{new Date(msg.created_at).toLocaleDateString(isAr ? "ar" : "en")}</span>
                                    )}
                                  </div>
                                  <div className="mfo-msg__bubble">
                                    {msg.message}
                                    {attachUrl ? (
                                      <div className="mt-2">
                                        {isImg ? (
                                          <a href={attachUrl} target="_blank" rel="noopener noreferrer" className="d-inline-block">
                                            <img
                                              src={attachUrl}
                                              alt={fileName}
                                              className="img-fluid rounded-3 border shadow-sm"
                                              style={{ maxHeight: "200px", objectFit: "cover" }}
                                            />
                                          </a>
                                        ) : (
                                          <a
                                            className="mfo-msg__attachment"
                                            href={attachUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            <Download size={12} />
                                            <span>{fileName}</span>
                                          </a>
                                        )}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="mfo-chat__empty">
                              <MessageCircle size={28} strokeWidth={1.5} />
                              <p>{isAr ? "لا توجد رسائل بعد" : "No messages yet"}</p>
                            </div>
                          )}
                        </div>

                        <div className="mfo-chat__compose">
                          <textarea
                            rows="3" className="mfo-input"
                            placeholder={isAr ? "اكتب رسالتك..." : "Type your message..."}
                            value={messageTexts[order.id] || ""}
                            onChange={(e) => setMessageTexts((c) => ({ ...c, [order.id]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey && (messageTexts[order.id] || "").trim()) {
                                e.preventDefault();
                                handleSendMessage(order.id);
                              }
                            }}
                          />
                          <div className="mfo-chat__compose-row">
                            <label className="mfo-file-label">
                              <Paperclip size={14} />
                              {messageFiles[order.id]
                                ? <span className="mfo-file-label__name">{messageFiles[order.id].name}</span>
                                : <span>{isAr ? "إرفاق ملف..." : "Attach file..."}</span>
                              }
                              <input
                                type="file" accept={SERVICE_ORDER_ATTACHMENT_ACCEPT} hidden
                                onChange={(e) => handleMessageFileChange(order.id, e.target.files?.[0] || null, e.target)}
                              />
                            </label>
                            <button
                              type="button" className="mfo-btn mfo-btn--primary"
                              disabled={actionOrderId === order.id || Boolean(messageFileErrors[order.id]) || (!messageTexts[order.id]?.trim() && !messageFiles[order.id])}
                              onClick={() => handleSendMessage(order.id)}
                            >
                              {actionOrderId === order.id
                                ? <><Loader2 size={14} className="mfo-spinner" />{isAr ? "جارِ الإرسال..." : "Sending..."}</>
                                : <><Send size={14} />{isAr ? "إرسال" : "Send"}</>
                              }
                            </button>
                          </div>
                          {messageFileErrors[order.id] && <div className="mfo-field-error"><AlertTriangle size={12} />{messageFileErrors[order.id]}</div>}
                        </div>
                      </section>

                      {/* Milestones + Financial 2-col */}
                      <div className="mfo-details-grid">

                        {/* Milestones */}
                        <section className="mfo-section">
                          <div className="mfo-section__head">
                            <Milestone size={16} />
                            <h3>{isAr ? "المعالم" : "Milestones"}</h3>
                            <span className="mfo-section__count">{milestones.length}</span>
                          </div>

                          {milestones.length > 0 ? (
                            <div className="mfo-milestone-list">
                              {milestones.map((m) => (
                                <div key={m.id} className={`mfo-milestone${m.status === "released" ? " mfo-milestone--released" : ""}`}>
                                  <div className="mfo-milestone__dot" />
                                  <div className="mfo-milestone__body">
                                    <div className="mfo-milestone__top">
                                      <strong>{m.title}</strong>
                                      <span className="mfo-badge mfo-badge--muted">{m.status}</span>
                                    </div>
                                    <div className="mfo-milestone__amount">
                                      {formatServiceCurrency(m.amount || 0, m.currency || order.currency || "USD")}
                                    </div>
                                    {m.description && <p className="mfo-milestone__desc">{m.description}</p>}
                                    <small className="mfo-milestone__note">
                                      {m.status === "released"
                                        ? (isAr ? "✓ تمت موافقة العميل وإطلاق المبلغ." : "✓ Client approved and amount released.")
                                        : (isAr ? "بانتظار موافقة العميل." : "Waiting for client approval.")}
                                    </small>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mfo-inline-empty">
                              <Milestone size={22} strokeWidth={1.5} />
                              <span>{isAr ? "لا توجد معالم بعد." : "No milestones yet."}</span>
                            </div>
                          )}

                          {milestoneEditingAllowed ? (
                            <div className="mfo-milestone-form">
                              <div className="mfo-milestone-form__head">{isAr ? "إضافة معلم جديد" : "Add New Milestone"}</div>
                              <input type="text" className="mfo-input" placeholder={isAr ? "عنوان المعلم" : "Milestone title"} value={milestoneDraft.title} onChange={(e) => updateMilestoneDraft(order.id, "title", e.target.value)} />
                              <div className="mfo-row-2">
                                <input type="number" min="0.01" step="0.01" className="mfo-input" placeholder={isAr ? "القيمة" : "Amount"} value={milestoneDraft.amount} onChange={(e) => updateMilestoneDraft(order.id, "amount", e.target.value)} />
                                <input type="datetime-local" className="mfo-input" value={milestoneDraft.dueAt} onChange={(e) => updateMilestoneDraft(order.id, "dueAt", e.target.value)} />
                              </div>
                              <textarea rows="2" className="mfo-input" placeholder={isAr ? "وصف المعلم (اختياري)" : "Description (optional)"} value={milestoneDraft.description} onChange={(e) => updateMilestoneDraft(order.id, "description", e.target.value)} />
                              <button type="button" className="mfo-btn mfo-btn--primary mfo-btn--sm" disabled={actionOrderId === order.id || !milestoneDraft.title.trim() || !milestoneDraft.amount} onClick={() => handleCreateMilestone(order.id)}>
                                {actionOrderId === order.id
                                  ? <><Loader2 size={13} className="mfo-spinner" />{isAr ? "جارِ الحفظ..." : "Saving..."}</>
                                  : <><Milestone size={13} />{isAr ? "إضافة معلم" : "Add Milestone"}</>
                                }
                              </button>
                            </div>
                          ) : (
                            <div className="mfo-info-note">
                              <Flag size={13} />
                              {isAr
                                ? "إضافة المعالم متاحة فقط للطلبات المدفوعة والنشطة."
                                : "Milestones are available only for active paid orders."}
                            </div>
                          )}
                        </section>

                        {/* Financial */}
                        <section className="mfo-section">
                          <div className="mfo-section__head">
                            <BarChart3 size={16} />
                            <h3>{isAr ? "الحالة المالية" : "Financial State"}</h3>
                          </div>

                          <div className="mfo-finance-stats">
                            <div className="mfo-finance-stat mfo-finance-stat--escrow">
                              <small>{isAr ? "محجوز في الضمان" : "Held in Escrow"}</small>
                              <strong>{formatServiceCurrency((order.payment_summary?.total_amount || 0) - (order.payment_summary?.released_amount || 0), order.payment_summary?.currency || order.currency || "USD")}</strong>
                            </div>
                            <div className="mfo-finance-stat mfo-finance-stat--released">
                              <small>{isAr ? "محرر لك" : "Released to You"}</small>
                              <strong>{formatServiceCurrency(order.payment_summary?.released_amount || 0, order.payment_summary?.currency || order.currency || "USD")}</strong>
                            </div>
                            <div className="mfo-finance-stat mfo-finance-stat--refund">
                              <small>{isAr ? "إجمالي المسترد" : "Refund Total"}</small>
                              <strong>{formatServiceCurrency(order.payment_summary?.refund_total || 0, order.payment_summary?.currency || order.currency || "USD")}</strong>
                            </div>
                          </div>

                          {refunds.length > 0 && (
                            <div className="mfo-refund-list">
                              {refunds.map((r) => (
                                <div key={r.id} className="mfo-refund-item">
                                  <strong>{formatServiceCurrency(r.amount || 0, r.currency || order.currency || "USD")}</strong>
                                  <span className="mfo-badge mfo-badge--muted">{r.status}</span>
                                  <p>{r.reason || (isAr ? "بدون سبب." : "No reason.")}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          {disputes.length > 0 ? (
                            <div className="mfo-dispute-list">
                              <div className="mfo-alert mfo-alert--warning mfo-alert--sm">
                                <AlertTriangle size={13} />
                                {isAr
                                  ? "عند فتح نزاع يتم إيقاف إجراءات الطلب مؤقتًا."
                                  : "When a dispute is opened, order actions are paused."}
                              </div>
                              {disputes.map((d) => (
                                <div key={d.id} className="mfo-dispute-card">
                                  <div className="mfo-dispute-card__top">
                                    <strong>{d.reason}</strong>
                                    <span className="mfo-badge mfo-badge--danger">{d.status}</span>
                                  </div>
                                  {d.details && <p className="mfo-dispute-card__details">{d.details}</p>}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mfo-inline-empty">
                              <ShieldCheck size={20} strokeWidth={1.5} />
                              <span>{isAr ? "لا توجد نزاعات على هذا الطلب." : "No disputes on this order."}</span>
                            </div>
                          )}

                          {/* Client Review */}
                          <div className="mfo-section__head" style={{ marginTop: "20px" }}>
                            <Star size={16} />
                            <h3>{isAr ? "تقييم العميل" : "Client Review"}</h3>
                          </div>
                          {order.review ? (
                            <div className="mfo-review">
                              <StarRating value={order.review.rating || 0} size={16} showValue />
                              {order.review.title && <strong className="mfo-review__title">{order.review.title}</strong>}
                              {order.review.comment && <p className="mfo-review__comment">{order.review.comment}</p>}
                            </div>
                          ) : (
                            <div className="mfo-inline-empty">
                              <Star size={20} strokeWidth={1.5} />
                              <span>{isAr ? "لا يوجد تقييم من العميل بعد." : "No review from the client yet."}</span>
                            </div>
                          )}
                        </section>
                      </div>{/* /mfo-details-grid */}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
